import { Box, Button, FormControl, Grid, MenuItem, Select, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material'
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { ReportPeriodContext } from '../../../context/report-periods/report-periods';
import gaiaTheme from '../../../root-components/theme';
import { TooltipButton } from '../../presentation-components/miscellaneous-components/tooltip-button';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { DashboardInformation } from '../../presentation-components/dashboard-components/dashboard-information';
import { DataOwnerTasksCard } from '../../presentation-components/dashboard-components/data-owner-tasks-card';
import { ReadinessCard } from '../../presentation-components/dashboard-components/readiness-card';
import { AnalyticsOverview } from '../../presentation-components/analytics-components/tabs/analytics-overview';
import { ClosedReportPeriodMessage } from '../../presentation-components/miscellaneous-components/closed-report-period-message';
import { StartNewReportPeriodDialog } from '../../dialogs/settings/report-period/start-new-report-period-dialog';
import { WelcomeDialog } from '../../dialogs/onboarding/welcome-dialog';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { useHasOpenReportPeriod } from '../../../hooks/use-has-open-report-period';
import { useShowWelcomeDialog } from '../../../hooks/use-show-welcome-dialog';
import AnalyticsService from '../../../shared/services/analytics/analytics.service';
import { analyticsScopeTwoMethodFromDefaultMarketBased } from '../../../shared/utils/default-market-based-utils';
import { isEntityScoped, userHasRequiredRole } from '../../../shared/utils/role-utils';
import LocationService from '../../../shared/services/location/location.service';
import { InformationBox } from '../../presentation-components/miscellaneous-components/information-box';
import { IGetStartedStep, useGetStartedSteps } from '../../../hooks/use-get-started-steps';
import { GetStartedCard } from '../../presentation-components/dashboard-components/get-started-card';
import { InvitedUserOrientationBanner } from '../../presentation-components/dashboard-components/invited-user-orientation-banner';

export const DashboardPage = () => {
  const analyticsService = new AnalyticsService();
  //Context
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const organisationDetails = userDetailsContext.userDetails?.organisation
  const reportPeriodContext = useContext(ReportPeriodContext);
  //Hooks
  const navigate = useNavigate();
  const welcome = useShowWelcomeDialog();
  const hasOpenReport = useHasOpenReportPeriod([
    reportPeriodContext.allReportPeriods,
    reportPeriodContext.lastSelectedReportId,
  ]);
  //Get-started guide: trial organisations only, admins only, driven by real org state
  //re-derived each load. Paying customers are past onboarding, so the guide (and its
  //state fetches) never run for them.
  const isAdmin = userHasRequiredRole(userDetails, ['admin']);
  const onFreeTrial = typeof organisationDetails?.freeTrialDaysRemaining === 'number'
    && organisationDetails.freeTrialDaysRemaining > 0;
  const getStarted = useGetStartedSteps(isAdmin && onFreeTrial && !!organisationDetails);
  const getStartedRef = useRef<HTMLDivElement>(null);
  //State
  const [data, setData] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scopedLocations, setScopedLocations] = useState<{ id: string, name: string }[]>([]);
  // '' means all the user's sites; otherwise one permitted location narrows every figure.
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  //Entity scope: the BE already filters every figure to the user's permitted entities; this
  //banner just says so. Zero assigned locations = the admin has not set the user up yet.
  const entityScoped = isEntityScoped(userDetails);
  const scopedToNothing = entityScoped
    && userDetails?.entityScope?.accessAllLocations === false
    && (userDetails.entityScope.locationIds || []).length === 0;

  useEffect(() => {
    if (!entityScoped || userDetails?.entityScope?.accessAllLocations !== false || scopedToNothing) {
      setScopedLocations([]);
      return;
    }
    const permitted = new Set(userDetails.entityScope.locationIds);
    new LocationService().getAllLocations()
      .then((locations: any[]) => {
        setScopedLocations(locations.filter(l => permitted.has(l.id)).map(l => ({ id: l.id, name: l.friendlyName })));
      })
      .catch(() => setScopedLocations([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails?.id, entityScoped]);
  //Layout
  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  //Variables
  const companyName = (!userDetails?.isPartner && userDetails?.partnerOrganisation) || (userDetails?.isPartner && userDetails?.isLoggedInAsClient) ? userDetails?.partnerOrganisation?.partnerName : 'Gaia';

  useEffect(() => {
    if (userDetails?.email.includes('@gaiacompany.io')) {
      if (organisationDetails?.trueSubscriptionActive) {
        setData('organisation');
      } else {
        setData('demo');
      }
      return;
    }
    if (reportPeriodContext.lastSelectedReportId) {
      const onFreeTrial =
        !!organisationDetails &&
        typeof organisationDetails.freeTrialDaysRemaining === 'number' &&
        organisationDetails.freeTrialDaysRemaining > 0;
      if (onFreeTrial) {
        analyticsService
          .getBreakdownTotal(
            reportPeriodContext.lastSelectedReportId,
            analyticsScopeTwoMethodFromDefaultMarketBased(organisationDetails?.defaultMarketBased),
            organisationDetails?.isFlagEnabled === true,
          )
          .then((response) => {
            setData(response.total > 0 ? 'organisation' : 'demo');
          })
          .catch((err) => {
            console.error(err);
            setData('organisation');
          });
      } else {
        setData('organisation');
      }
    } else if (!reportPeriodContext.lastSelectedReportId && organisationDetails && typeof organisationDetails.freeTrialDaysRemaining === 'number' && organisationDetails.freeTrialDaysRemaining > 0) {
      setData('demo');
    } else {
      setData('organisation');
    }
  }, [dialogOpen, organisationDetails, reportPeriodContext.allReportPeriods, reportPeriodContext.lastSelectedReportId, userDetails?.email]);

  const dataHandler = (
    event: React.MouseEvent<HTMLElement>,
    data: string | null,
  ) => {
    if (data) {
      setData(data);
    }
  };

  // The reporting-period step opens the existing dialog in place; every other
  // step deep-links to where the work happens.
  const handleGetStartedStep = (step: IGetStartedStep) => {
    if (step.id === 'reporting-period') {
      setDialogOpen(true);
    } else {
      navigate(step.path);
    }
  };

  // The Welcome dialog's primary CTA lands here: make sure the guide is showing
  // and bring it into view.
  const showGetStartedGuide = () => {
    getStarted.show();
    setTimeout(() => getStartedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  // The guide already carries "Start a reporting period", so the closed-period
  // box would say the same thing twice for a brand-new org.
  const periodStepOutstanding = getStarted.visible
    && getStarted.steps.some(step => step.id === 'reporting-period' && !step.done);

  return (
    <PageOuterContainer>
      <Grid container spacing={1} sx={{ mb: organisationDetails && typeof organisationDetails.freeTrialDaysRemaining === 'number' && organisationDetails.freeTrialDaysRemaining > 0 ? 1 : 2 }}>
        <Grid item
          xl={(organisationDetails && typeof organisationDetails.freeTrialDaysRemaining === 'number' && organisationDetails.freeTrialDaysRemaining > 0) || (organisationDetails && userDetails?.email.includes('@gaiacompany.io')) ? 8 : 12}
          lg={(organisationDetails && typeof organisationDetails.freeTrialDaysRemaining === 'number' && organisationDetails.freeTrialDaysRemaining > 0) || (organisationDetails && userDetails?.email.includes('@gaiacompany.io')) ? 8 : 12}
          md={(organisationDetails && typeof organisationDetails.freeTrialDaysRemaining === 'number' && organisationDetails.freeTrialDaysRemaining > 0) || (organisationDetails && userDetails?.email.includes('@gaiacompany.io')) ? 8 : 12}
          sm={12} xs={12} >
          <Typography variant='h4' sx={{ color: 'text.primary', fontWeight: 700 }}> {organisationDetails?.name} </Typography>
          {entityScoped && !scopedToNothing && scopedLocations.length === 1 &&
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
              Showing data for: {scopedLocations[0].name}
            </Typography>
          }
          {/* More than one permitted site: a switcher narrows every dashboard figure to one of them. */}
          {entityScoped && !scopedToNothing && scopedLocations.length > 1 &&
            <Stack direction='row' alignItems='center' spacing={1} sx={{ mt: 0.5 }}>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Showing data for:
              </Typography>
              <FormControl variant='standard' size='small'>
                <Select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  displayEmpty
                  disableUnderline
                  sx={{ fontSize: 'body2.fontSize', color: 'text.primary', '& .MuiSelect-select': { py: 0 } }}>
                  <MenuItem value=''>All your sites</MenuItem>
                  {scopedLocations.map(location =>
                    <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Stack>
          }
          {/* Subsidiary-only scoping (all locations, restricted subsidiaries): no location names
              to list, so a generic line. */}
          {entityScoped && !scopedToNothing && scopedLocations.length === 0
            && userDetails?.entityScope?.accessAllLocations !== false &&
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
              Figures are filtered to the entities assigned to you
            </Typography>
          }
        </Grid>
        {scopedToNothing &&
          <Grid item xs={12}>
            <InformationBox
              icon={IconName.InfoOutlined}
              text="Your administrator hasn't assigned you a location yet, so there is nothing to show here."
              additionalText='Ask them to add you to one in Settings > Users.'
              marginTop={1}
            />
          </Grid>
        }
        {((userDetails?.email.includes('@gaiacompany.io') && organisationDetails?.trueSubscriptionActive === false)
          ||
          (organisationDetails && typeof organisationDetails.freeTrialDaysRemaining === 'number' && organisationDetails.freeTrialDaysRemaining > 0)
        ) &&
          <Grid item xl={4} lg={4} md={4} sm={12} xs={12}>
            <Stack direction={isMobileModeMd ? 'column' : 'row'} justifyContent='flex-end' spacing={2} sx={{ width: '100%', mb: 2 }}>
              <>
                {!isMobileModeMd &&
                  <TooltipButton
                    noPadding={true}
                    text='Demo data is available to showcase the dashboard features. You can easily switch between the demo data and your organisations data.'
                    placement='left' />}
                <ToggleButtonGroup
                  value={data}
                  exclusive
                  onChange={dataHandler}
                  orientation={isMobileModeMd ? "vertical" : 'horizontal'}>
                  <ToggleButton value="demo" sx={{ width: !isMobileModeMd ? '175px' : '100%', pt: 1, pb: 1, textTransform: 'capitalize' }}>
                    <Typography>Demo Data</Typography>
                  </ToggleButton>
                  <ToggleButton value="organisation" sx={{
                    width: !isMobileModeMd ? '175px' : '100%', p: 1, textTransform: 'none',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                    textAlign: 'center'
                  }}>
                    <Typography noWrap>{organisationDetails.name} Data</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
              </>
            </Stack>
          </Grid>}
        {!userDetails?.organisation?.subscriptionActive &&
          <DashboardInformation
            title={'Your free trial has ended'}
            subtitle={`Your trial is over, but you can continue using ${companyName} Carbon Accounting by upgrading to our paid version.`}
            bulletPoints={[
              'Record all your emissions aligned to the GHG Protocol Corporate Standard',
              'Seamlessly sync data from your other systems or import from a spreadsheet',
              'Generate your UK Streamlined Energy and Carbon Report',
              'Access advanced analytics to track your emissions progress over time',
              'Generate a tailored carbon reduction plan with clear, actionable steps to meet your business objectives',
              `Offset your emissions with exclusive access to ${(!userDetails?.isPartner && userDetails?.partnerOrganisation) || (userDetails?.isPartner && userDetails?.isLoggedInAsClient) ? 'the' : 'Gaia\'s'} industry leading natural capital marketplace`
            ]}
            buttonTextOne={`Contact ${companyName} sales team`}
            emailAddress={(!userDetails?.isPartner && userDetails?.partnerOrganisation) || (userDetails?.isPartner && userDetails?.isLoggedInAsClient) ? userDetails?.partnerOrganisation?.partnerEmailAddress || 'sales@gaiacompany.io' : 'sales@gaiacompany.io'}
            buttonTextTwo={'Upgrade to paid version'}
            icon={IconName.Done} />}
      </Grid>

      <InvitedUserOrientationBanner />

      {getStarted.visible &&
        <Box ref={getStartedRef}>
          <GetStartedCard
            steps={getStarted.steps}
            firstName={userDetails?.firstName}
            onStepAction={handleGetStartedStep}
            onSkipInvite={getStarted.skipInvite}
            onHide={getStarted.hide}
          />
        </Box>}
      {getStarted.reopenAvailable &&
        <Stack direction='row' justifyContent='flex-end' sx={{ mb: 1 }}>
          <Button variant='text' size='small' onClick={showGetStartedGuide}>
            Setup guide
          </Button>
        </Stack>}


      {data === 'organisation' && hasOpenReport === false && !periodStepOutstanding && (
        <ClosedReportPeriodMessage openDialog={() => setDialogOpen(true)} />
      )}

      {dialogOpen && (
        <StartNewReportPeriodDialog
          open={dialogOpen}
          closeHandler={() => setDialogOpen(false)}
        />
      )}

      <WelcomeDialog
        open={welcome.open}
        firstName={userDetails?.firstName}
        onDismiss={welcome.close}
        onNavigate={(path) => navigate(path)}
        onStartReportPeriod={() => setDialogOpen(true)}
        onShowGetStarted={showGetStartedGuide}
      />

      {/* Admins and analysts get the readiness strip; entity-scoped users keep their My Tasks
          card as the companion tile below instead. Hidden in demo mode: readiness evaluates the
          organisation's real data. */}
      {data !== 'demo' && !entityScoped
        && (userDetails?.roles.includes('admin') || userDetails?.roles.includes('analyst'))
        && <ReadinessCard />}

      <AnalyticsOverview
        emissionType="marketBased"
        dummyData={data === 'demo'}
        locationId={selectedLocationId || undefined}
        companionTile={entityScoped && !scopedToNothing ? <DataOwnerTasksCard locationId={selectedLocationId || undefined} /> : undefined}
      />
      {/* </div>
        </Fade>
      )} */}


    </PageOuterContainer>)
}